import { expect, fixture } from '@open-wc/testing';
import { html } from 'lit';
import { IxGenericTree } from '../IxGenericTree.js';
import { IxGenericTreeNode } from '../IxGenericTreeNode.js';
import { IxGenericTreeTestIds } from '../IxGenericTreeTestIds.js';
import type { IGenericTreeNode } from '../ix-generic-tree-types.js';
import ixGenericTreeTestData from './test-data.js';
import '../ix-generic-tree.js';
import '../ix-generic-tree-node.js';

const selectorTestId = <T extends HTMLElement>(
  element: IxGenericTree,
  id: string,
): T | undefined => {
  let target = element.shadowRoot?.querySelector<T>(`[data-testid="${id}"]`);
  if (target) return target;

  const nodes =
    element.shadowRoot?.querySelectorAll<IxGenericTreeNode>(
      'ix-generic-tree-node',
    ) ?? [];
  for (const n of nodes) {
    target = n.shadowRoot!.querySelector<T>(`[data-testid="${id}"]`);
    if (target) return target;
  }

  return undefined;
};

const replaceNodeKey = (
  data: IGenericTreeNode,
  key: keyof IGenericTreeNode,
  value: any,
) => {
  /* eslint-disable no-param-reassign */
  // @ts-ignore
  data[key] = value;
  if (data.children?.length) {
    data.children.forEach(child => replaceNodeKey(child, key, value));
  }
  return data;
};

const extractNodeValues = <T>(
  data: IGenericTreeNode,
  key: keyof IGenericTreeNode,
) => {
  const values: T[] = [];

  if (data[key]) {
    // @ts-ignore
    values.push(data[key]);
  }
  if (data.children?.length) {
    data.children.forEach(child =>
      values.push(...extractNodeValues<T>(child, key)),
    );
  }
  return values;
};

const extractNodeValuesAtDepth = <T>(
  data: IGenericTreeNode,
  key: keyof IGenericTreeNode,
  depth: number,
) => {
  const values: T[] = [];

  if (data[key] !== undefined && depth === 0) {
    // @ts-ignore
    values.push(data[key]);
  }
  if (data.children?.length) {
    data.children.forEach(child =>
      values.push(...extractNodeValuesAtDepth<T>(child, key, depth - 1)),
    );
  }
  return values;
};

describe('<ix-generic-tree>', () => {
  it('should render the search box correctly', async () => {
    const element = await fixture<IxGenericTree>(
      html`<ix-generic-tree
        .rootNode=${ixGenericTreeTestData}
        searchEnabled
        searchPlaceholder="placeholder"
      ></ix-generic-tree>`,
    );

    let searchBox = selectorTestId<HTMLInputElement>(
      element,
      IxGenericTreeTestIds.SEARCH_BOX,
    );
    expect(searchBox, IxGenericTreeTestIds.SEARCH_BOX).to.exist;
    await expect(searchBox?.placeholder, 'placeholder').to.equal('placeholder');

    element.searchEnabled = false;
    await element.updateComplete;
    searchBox = selectorTestId<HTMLInputElement>(
      element,
      IxGenericTreeTestIds.SEARCH_BOX,
    );
    expect(searchBox, IxGenericTreeTestIds.SEARCH_BOX).to.not.exist;
  });

  it('should render the check boxes correctly', async () => {
    const data = replaceNodeKey(
      structuredClone(ixGenericTreeTestData),
      'expanded',
      true,
    );

    const element = await fixture<IxGenericTree>(
      html`<ix-generic-tree
        .rootNode=${data}
        showCheckboxes
        searchEnabled
        searchPlaceholder="placeholder"
      ></ix-generic-tree>`,
    );

    const ids = extractNodeValues<string>(data, 'id').filter(
      id => id !== 'root',
    );

    // Checkboxes exist and not disabled
    for (const id of ids) {
      const targetId = IxGenericTreeTestIds.CHECK_BOX_ID(id);
      const checkbox = selectorTestId<HTMLInputElement>(element, targetId);
      expect(checkbox, targetId).to.exist;
      expect(checkbox?.disabled, targetId).to.be.false;
    }

    element.disableCheckboxes = true;
    await element.updateComplete;

    // Checkboxes exist and are disabled
    for (const id of ids) {
      const targetId = IxGenericTreeTestIds.CHECK_BOX_ID(id);
      const checkbox = selectorTestId<HTMLInputElement>(element, targetId);
      expect(checkbox, targetId).to.exist;
      expect(checkbox?.disabled, targetId).to.be.true;
    }

    element.showCheckboxes = false;
    await element.updateComplete;

    // Checkboxes hidden
    for (const id of ids) {
      const targetId = IxGenericTreeTestIds.CHECK_BOX_ID(id);
      const checkbox = selectorTestId<HTMLInputElement>(element, targetId);
      expect(checkbox, targetId).to.not.exist;
    }
  });

  it('should expand the nodes and render correct labels', async () => {
    const element = await fixture<IxGenericTree>(
      html`<ix-generic-tree
        .rootNode=${ixGenericTreeTestData}
        showCheckboxes
        searchEnabled
        searchPlaceholder="placeholder"
      ></ix-generic-tree>`,
    );

    const ids = extractNodeValues<string>(ixGenericTreeTestData, 'id').filter(
      id => id !== 'root',
    );

    let nodes: IxGenericTreeNode[] = [];

    function getNodes() {
      const _nodes: IxGenericTreeNode[] = [];
      for (const id of ids) {
        const node = selectorTestId<IxGenericTreeNode>(
          element,
          IxGenericTreeTestIds.NODE_ID(id),
        );
        if (node) _nodes.push(node);
      }
      nodes = _nodes;
    }

    async function testLabel(id: string, label?: string) {
      const targetId = IxGenericTreeTestIds.NODE_LABEL(id);
      const target = selectorTestId<HTMLParagraphElement>(element, targetId);
      expect(target, targetId).to.exist;
      await expect(target?.innerText, label).to.eq(label);
      target?.click();
    }

    async function testCount(id: string, count: number) {
      const targetId = IxGenericTreeTestIds.NODE_COUNT(id);
      const target = selectorTestId<HTMLParagraphElement>(element, targetId);
      expect(target, targetId).to.exist;

      // A count of zero is not rendered
      await expect(target?.innerText, targetId).to.eq(
        count ? count.toString(10) : '',
      );
    }
    /* eslint-disable no-await-in-loop */
    for (let i = 1; i < 5; i += 1) {
      getNodes();
      const _ids = extractNodeValuesAtDepth<string>(
        ixGenericTreeTestData,
        'id',
        i,
      );
      const labels = extractNodeValuesAtDepth<string>(
        ixGenericTreeTestData,
        'label',
        i,
      );
      const counts = extractNodeValuesAtDepth<number>(
        ixGenericTreeTestData,
        'childrenCount',
        i,
      );

      if (i !== 4) {
        await expect(nodes.length, `level-${i}`).to.eq(i);
        await testLabel(_ids.at(-1)!, labels.at(-1));
        await testCount(_ids.at(-1)!, counts.at(-1)!);
        await element.updateComplete;
      } else {
        // Last node has 2 children
        await expect(nodes.length, `level-${i}`).to.eq(i + 1);
        await testLabel(_ids.at(-1)!, labels.at(-1));
        await testLabel(_ids.at(-2)!, labels.at(-2));
        await testCount(_ids.at(-1)!, counts.at(-1)!);
        await testCount(_ids.at(-2)!, counts.at(-2)!);
      }
    }
  });
});
